import { Link, useNavigate } from 'react-router-dom';
import { eventPnlPath } from '@/lib/event-pnl';
import { DataTable, type DataTableCard, type DataTableHeader, type DataTableSort } from '@bcecloud/portal-ui';
import { EventPnlNameLink } from '@/components/event-pnl';
import {
  BASIS_LABELS,
  eventDateLabel,
  facilitatorPersonPath,
  salesforceStatementUrl,
  spendMoney,
  timesMedianLabel,
  unitMoney,
  type FlagRow,
} from '@/lib/facilitator-spend';

/** Sort keys for the flagged-lines table (FlagRow fields). */
export const FLAG_SORT_FIELDS = {
  person: 'personName',
  category: 'categoryLabel',
  event: 'opportunityName',
  eventDate: 'eventDate',
  amount: 'amount',
  timesMedian: 'timesMedian',
  excess: 'excess',
} as const;
export const FLAG_SORT_KEYS = Object.values(FLAG_SORT_FIELDS);
/** Largest gap above the norm first. */
export const FLAG_DEFAULT_SORT = { key: FLAG_SORT_FIELDS.excess, direction: 'desc' } as const;
export const FLAG_SEARCH_FIELDS = [
  'personName',
  'categoryLabel',
  'opportunityName',
  'accountName',
  'program',
  'description',
] as const satisfies ReadonlyArray<keyof FlagRow>;

function lineLabel(row: FlagRow): string {
  const description = row.description.trim();
  return description && description.toLowerCase() !== row.categoryLabel.toLowerCase() ? description : '';
}

/** "per trip", or "$470 per night × 3" when the line covers several units. */
function perUnitLabel(row: FlagRow): string {
  const unit = BASIS_LABELS[row.flag.basis];
  return row.flag.units > 1 ? `${unitMoney(row.costPerUnit)} ${unit} × ${row.flag.units}` : unit;
}

/** The flag line and, for a program's own norm, which program it came from. */
function normDetail(row: FlagRow): string {
  const { norm } = row.flag;
  const scope = norm.scope === 'program' && norm.program ? `${norm.program}, ` : '';
  return `Look above ${unitMoney(norm.threshold)} · ${scope}${norm.sampleSize.toLocaleString('en-US')} lines`;
}

function SourceLinks({ row }: { readonly row: FlagRow }): React.ReactElement {
  const receiptUrl = row.flag.receipt?.receiptUrl ?? null;
  return (
    <span className="flex flex-col gap-0.5">
      <a href={salesforceStatementUrl(row.flag.statementId)} target="_blank" rel="noreferrer" className="text-brand hover:underline">
        Statement
      </a>
      {receiptUrl ? (
        <a href={receiptUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
          Receipt
        </a>
      ) : (
        <span className="text-portal-micro text-portal-muted">{row.flag.receipt ? 'No receipt for this line' : 'Entered in Salesforce'}</span>
      )}
    </span>
  );
}

export interface FacilitatorFlagsTableProps {
  readonly rows: readonly FlagRow[];
  readonly sort: DataTableSort | undefined;
  readonly onSort: (key: string) => void;
  /** The list across people shows who billed the line; a person's page does not. */
  readonly showPerson: boolean;
  /** Carried onto person links so the window survives the click. */
  readonly linkSearch?: string;
}

/**
 * Lines worth a look: each with what was billed, the norm it was compared
 * with and how far above it sits, and links to the Salesforce statement and,
 * where the payment came through bcecloud, the receipt.
 */
export function FacilitatorFlagsTable({ rows, sort, onSort, showPerson, linkSearch = '' }: FacilitatorFlagsTableProps): React.ReactElement {
  const headers: DataTableHeader[] = [
    ...(showPerson ? [{ label: 'Person', sortKey: FLAG_SORT_FIELDS.person }] : []),
    { label: 'Line', sortKey: FLAG_SORT_FIELDS.category },
    { label: 'Event', sortKey: FLAG_SORT_FIELDS.event },
    { label: 'Event date', sortKey: FLAG_SORT_FIELDS.eventDate },
    { label: 'Amount', sortKey: FLAG_SORT_FIELDS.amount },
    'Typical',
    { label: 'vs median', sortKey: FLAG_SORT_FIELDS.timesMedian },
    { label: 'Above the norm', sortKey: FLAG_SORT_FIELDS.excess },
    'Source',
  ];
  const offset = showPerson ? 1 : 0;
  const navigate = useNavigate();
  const cards: DataTableCard[] = rows.map((row) => ({
    key: row.key,
    title: `${row.categoryLabel}${showPerson ? `, ${row.personName}` : ''}`,
    subtitle: `${row.opportunityName} · ${eventDateLabel(row.eventDate)}`,
    onOpen: () => navigate(eventPnlPath(row.flag.opportunityId)),
    fields: [
      { label: 'Amount', value: spendMoney(row.amount) },
      { label: 'Typical', value: `${unitMoney(row.median)} median` },
      { label: 'vs median', value: timesMedianLabel(row.timesMedian) },
      { label: 'Above the norm', value: spendMoney(row.excess) },
      { label: 'Receipt', value: row.flag.receipt?.receiptUrl ? 'Linked' : 'None' },
    ],
  }));
  return (
    <DataTable
      headers={headers}
      rightAlignedHeaders={[4 + offset, 6 + offset, 7 + offset]}
      sort={sort}
      onSort={onSort}
      minWidth={showPerson ? 1180 : 1040}
      cards={cards}
    >
      {rows.map((row) => (
        <tr key={row.key} className="border-t border-portal-line align-middle">
          {showPerson ? (
            <td className="p-3 text-portal-small">
              <Link
                to={{ pathname: facilitatorPersonPath(row.contactId), search: linkSearch }}
                className="font-bold text-brand hover:underline"
              >
                {row.personName}
              </Link>
            </td>
          ) : null}
          <td className="p-3 text-portal-small">
            <span className="block font-bold text-portal-ink">{row.categoryLabel}</span>
            {lineLabel(row) ? <span className="block text-portal-micro text-portal-muted">{lineLabel(row)}</span> : null}
          </td>
          <td className="p-3 text-portal-small">
            <EventPnlNameLink opportunityId={row.flag.opportunityId} name={row.opportunityName} />
            <span className="block text-portal-micro text-portal-muted">{row.program}</span>
          </td>
          <td className="whitespace-nowrap p-3 text-portal-small text-portal-muted">{eventDateLabel(row.eventDate)}</td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
            <span className="block font-bold text-portal-ink">{spendMoney(row.amount)}</span>
            <span className="block text-portal-micro text-portal-muted">{perUnitLabel(row)}</span>
          </td>
          <td className="p-3 text-portal-small">
            <span className="block whitespace-nowrap">{unitMoney(row.median)} median</span>
            <span className="block text-portal-micro text-portal-muted">{normDetail(row)}</span>
          </td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums text-amber-800">
            {timesMedianLabel(row.timesMedian)}
          </td>
          <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums text-portal-muted">{spendMoney(row.excess)}</td>
          <td className="whitespace-nowrap p-3 text-portal-small">
            <SourceLinks row={row} />
          </td>
        </tr>
      ))}
    </DataTable>
  );
}
